import { getValueByPath } from "../mongodb-filter";
import { isComputedEnduserField, resolveComputedEnduserField, resolveIntakeFieldValue } from "./computed-tokens";
import type { FallbackItem, ParsedVariable, TemplateContext } from "./types";

/**
 * Matches a full template variable including any fallback chain and conditional suffix. Supports:
 * - `{{source.field}}`
 * - `{{source.field||"fallback"}}` / `{{source.field||source2.field2}}` (chained, literal-terminated)
 * - `{{source.field?"text"}}` and `{{source.field?"truthy":"falsy"}}`
 */
const TEMPLATE_REGEX =
  /\{\{(\w+)\.(\w+(?:\.\w+)*)((?:\|\|(?:"[^"]*"|\w+\.\w+(?:\.\w+)*))*)?(\?"([^"]*)"(?::"([^"]*)")?)??\}\}/g;

/** Matches one item of a fallback chain — either `"literal"` or `source.field`. */
const FALLBACK_ITEM_REGEX = /\|\|(?:"([^"]*)"|(\w+)\.(\w+(?:\.\w+)*))/g;

/**
 * Whether a resolved value counts as "missing" for fallbacks and conditionals.
 * `0` and `false` are NOT missing — only undefined, null, empty string and NaN.
 */
export const isMissing = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value === "") return true;
  if (typeof value === "number" && Number.isNaN(value)) return true;
  return false;
};

const parseFallbackChain = (fallbackChain: string): FallbackItem[] =>
  Array.from(fallbackChain.matchAll(new RegExp(FALLBACK_ITEM_REGEX.source, "g"))).reduce<FallbackItem[]>(
    (acc, match) => {
      if (match[1] !== undefined) return [...acc, { type: "literal", value: match[1] }];
      if (match[2] && match[3]) return [...acc, { type: "field", value: "", source: match[2], field: match[3] }];
      return acc;
    },
    []
  );

/** Every template variable in `text`, in source order. */
export const parseTemplateVariables = (text: string): ParsedVariable[] =>
  Array.from(text.matchAll(new RegExp(TEMPLATE_REGEX.source, "g"))).map((match) => {
    const variable: ParsedVariable = { source: match[1] ?? "", field: match[2] ?? "", raw: match[0] };

    const fallbackChain = match[3];
    if (fallbackChain) {
      variable.operator = "fallback";
      variable.fallbacks = parseFallbackChain(fallbackChain);
      const [firstFallback] = variable.fallbacks;
      if (variable.fallbacks.length === 1 && firstFallback?.type === "literal") {
        variable.operatorText = firstFallback.value;
      }
    }

    if (match[4]) {
      variable.operator = "conditional";
      variable.operatorText = match[5];
      if (match[6] !== undefined) variable.falsyText = match[6];
    }

    return variable;
  });

export const hasTemplateVariables = (text: string | undefined | null): boolean =>
  !!text && new RegExp(TEMPLATE_REGEX.source, "g").test(text);

/** Unique source prefixes used in `text`. Useful for deciding what data to fetch. */
export const extractTemplateSources = (text: string): Set<string> =>
  new Set(parseTemplateVariables(text).map((v) => v.source));

/**
 * An enduser field, falling back to `enduser.fields[name]` so custom fields resolve by bare name.
 * Computed fields (BMI, Age) are derived rather than read off the record.
 *
 * When `context.responses` is present, an intake answer from the form in progress outranks the
 * record, so a token echoes what the enduser just typed rather than a stale or empty stored value.
 */
const getEnduserFieldValue = (field: string, context: TemplateContext): unknown => {
  if (isComputedEnduserField(field)) {
    return resolveComputedEnduserField(field, { enduser: context.enduser, responses: context.responses });
  }

  if (context.responses?.length) {
    const intakeValue = resolveIntakeFieldValue(field, context.responses);
    if (intakeValue !== undefined) return intakeValue;
  }

  const { value: directValue } = getValueByPath(context.enduser, field);
  if (directValue !== undefined) return directValue;

  if (!field.includes(".")) {
    const { value: customFieldValue } = getValueByPath(context.enduser, `fields.${field}`);
    if (customFieldValue !== undefined) return customFieldValue;
  }

  return undefined;
};

const getSourceFieldValue = (source: string, field: string, context: TemplateContext): unknown => {
  switch (source) {
    case "enduser":
      if (!context.enduser && !context.responses?.length) return undefined;
      return getEnduserFieldValue(field, context);
    case "organization":
      if (!context.organization) return undefined;
      return getValueByPath(context.organization, field).value;
    case "snippet":
      return context.snippets?.[field];
    default:
      return undefined;
  }
};

/**
 * Replaces each template variable via `resolve`, which receives the parsed source and field and
 * returns the replacement — or `undefined` to leave the variable untouched. For second-pass
 * resolution (e.g. click-time async values) that must match exactly what {@link interpolateText}
 * recognizes, rather than re-deriving the pattern.
 */
export const replaceTemplateVariables = (
  text: string,
  resolve: (source: string, field: string) => string | undefined
): string =>
  text.replace(new RegExp(TEMPLATE_REGEX.source, "g"), (raw, source, field) => resolve(source, field) ?? raw);

export interface InterpolateOptions {
  /**
   * Sources to leave untouched, returning the raw `{{…}}` verbatim for a later pass. Used by
   * surfaces that resolve some sources asynchronously at click time.
   */
  deferredSources?: ReadonlySet<string>;
  /**
   * Entity-escape substituted values. Set this whenever the result is rendered as HTML: the values
   * come from the enduser's own record and answers, so without escaping a patient could inject
   * markup into otherwise admin-authored content.
   */
  escapeValues?: boolean;
}

const escapeHTML = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Replaces every `{{source.field}}` in `text` with its resolved value. Unresolvable variables become
 * an empty string (after exhausting any fallback chain), so a template never leaks its own syntax to
 * the reader.
 *
 * Sources are `enduser` (incl. custom fields and the computed `BMI`/`Age`), `organization`, and
 * `snippet`. Pass `escapeValues` when the result is rendered as HTML.
 */
export const interpolateText = (
  text: string | undefined | null,
  context: TemplateContext,
  options?: InterpolateOptions
): string => {
  if (!text) return "";

  const { deferredSources, escapeValues } = options ?? {};
  const emit = (value: string) => (escapeValues ? escapeHTML(value) : value);

  return text.replace(
    new RegExp(TEMPLATE_REGEX.source, "g"),
    (raw, source, field, fallbackChain, conditionalMatch, conditionalText, falsyText) => {
      if (deferredSources?.has(source)) return raw;

      const value = getSourceFieldValue(source, field, context);

      if (conditionalMatch) {
        if (isMissing(value)) return falsyText !== undefined ? emit(falsyText) : "";
        return emit(conditionalText);
      }

      if (!isMissing(value)) return emit(String(value));

      if (fallbackChain) {
        const fallback = parseFallbackChain(fallbackChain).find((item) => {
          if (item.type === "literal") return true;
          if (!item.source || !item.field) return false;
          if (deferredSources?.has(item.source)) return false;
          return !isMissing(getSourceFieldValue(item.source, item.field, context));
        });

        if (fallback?.type === "literal") return emit(fallback.value);
        if (fallback?.source && fallback.field) {
          return emit(String(getSourceFieldValue(fallback.source, fallback.field, context)));
        }
      }

      return "";
    }
  );
};
